import { defineStore } from 'pinia';
import { computed, ref, toRaw } from 'vue';
import { Frequency, WeekdayStr } from 'rrule';
import dayjs from 'dayjs';
import {
    IScheduledCoachJob,
    SCHEDULE_COACHING_VISIBILITY_MODE,
} from '../services/api/scheduleCoachingForm/types';
import { formatRRuleToInterval } from '../components/interactions/scheduleCoaching/scheduleCoaching.utils';
import { dateToISO } from '../utils/dateUtils';
import { useFilterStoreByName } from './filter';
import { FilterStoreName } from '../services/api/filter/types';
import { clearCheckboxSelection } from '../services/api/filter/utils';

export type ScheduleCoachingFormData = {
    description: string;
    title: string;
    formId: string;
    interactionsPerRun: number;
    visibilityMode: SCHEDULE_COACHING_VISIBILITY_MODE;
    intervals: ScheduleCoachingFormInterval[];
};

export type ScheduleCoachingFormInterval = {
    startDate: string;
    endDate: string;
    frequency: Frequency;
    interval: number;
    byweekday: Record<WeekdayStr, boolean>;
};

const DEFAULT_INTERVAL: ScheduleCoachingFormInterval = {
    startDate: dayjs().format('YYYY-MM-DD'),
    endDate: '',
    frequency: Frequency.WEEKLY,
    interval: 1,
    byweekday: {
        MO: true,
        TU: false,
        WE: false,
        TH: false,
        FR: false,
        SA: false,
        SU: false,
    },
};

const DEFAULT_FORM_DATA: ScheduleCoachingFormData = {
    description: '',
    title: '',
    formId: '',
    interactionsPerRun: 1,
    visibilityMode: SCHEDULE_COACHING_VISIBILITY_MODE.ALL_AUTHORIZED_USERS,
    intervals: [{ ...DEFAULT_INTERVAL }],
};

export const useScheduleCoaching = defineStore('scheduleCoaching', () => {
    const open = ref<boolean>(false);

    const editFormId = ref<string | null>(null);

    const formData = ref<ScheduleCoachingFormData>({ ...DEFAULT_FORM_DATA });

    const isValid = computed(() => {
        if (!formData.value.formId) return false;
        if (!formData.value.title) return false;
        if (formData.value.interactionsPerRun < 1) return false;

        if (formData.value.intervals.length === 0) return false;

        let validIntervals = true;

        formData.value.intervals.forEach((interval) => {
            if (!interval.startDate) {
                validIntervals = false;
            }

            if (interval.endDate && dayjs(interval.startDate).isAfter(dayjs(interval.endDate))) {
                validIntervals = false;
            }

            if (interval.interval < 1) {
                validIntervals = false;
            }

            if (interval.frequency === Frequency.WEEKLY) {
                const selectedDays = Object.values(interval.byweekday).filter((v) => v);
                if (selectedDays.length === 0) {
                    validIntervals = false;
                }
            }
        });
        return validIntervals;
    });

    // A form filter only matches interactions that already have a protocol, and the job only
    // coaches interactions without one, so a job saved with it would never select anything.
    const clearFormFilter = () => {
        const filterStore = useFilterStoreByName(FilterStoreName.ScheduleCoachingFilter);
        clearCheckboxSelection(filterStore.all?.protocolFormDefinitionFilter ?? []);
    };

    const openScheduleCoaching = () => {
        const filterStore = useFilterStoreByName(FilterStoreName.ScheduleCoachingFilter);
        filterStore.seedFromShared();
        clearFormFilter();
        open.value = true;
    };

    const closeScheduleCoaching = () => {
        resetFormData();
        const filterStore = useFilterStoreByName(FilterStoreName.ScheduleCoachingFilter);
        filterStore.onlyResolvedBothCheckedAsNull = false;
        open.value = false;
    };

    const resetFormData = () => {
        formData.value.description = DEFAULT_FORM_DATA.description;
        formData.value.title = DEFAULT_FORM_DATA.title;
        formData.value.formId = DEFAULT_FORM_DATA.formId;
        formData.value.interactionsPerRun = DEFAULT_FORM_DATA.interactionsPerRun;
        formData.value.visibilityMode = DEFAULT_FORM_DATA.visibilityMode;
        formData.value.intervals = DEFAULT_FORM_DATA.intervals.map((interval) => ({
            startDate: interval.startDate,
            endDate: interval.endDate,
            frequency: interval.frequency,
            interval: interval.interval,
            byweekday: {
                MO: true,
                TU: false,
                WE: false,
                TH: false,
                FR: false,
                SA: false,
                SU: false,
            },
        }));
        editFormId.value = null;
    };

    const addInterval = () => {
        formData.value.intervals.push({ ...DEFAULT_INTERVAL });
    };

    const openEdit = (data: IScheduledCoachJob) => {
        const filterStore = useFilterStoreByName(FilterStoreName.ScheduleCoachingFilter);
        filterStore.onlyResolvedBothCheckedAsNull = false;
        filterStore.loadFilter(structuredClone(toRaw(data.filter)));
        clearFormFilter();
        editFormId.value = data.id;
        formData.value.description = data.description;
        formData.value.title = data.title;
        formData.value.formId = data.formId;
        formData.value.interactionsPerRun = data.interactionsPerRun;
        formData.value.visibilityMode = data.visibilityMode;
        formData.value.intervals = data.intervals.map((interval) => {
            return {
                startDate: dateToISO(interval.startDate),
                endDate: interval.endDate ? dateToISO(interval.endDate) : '',
                ...formatRRuleToInterval(interval.rRule),
            };
        });
        open.value = true;
    };

    return {
        open,
        closeScheduleCoaching,
        openScheduleCoaching,
        addInterval,
        formData,
        isValid,
        openEdit,
        editFormId,
    };
});
